import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { EditorState } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
  keymap,
  lineNumbers
} from '@codemirror/view';
import { useEffect, useRef } from 'react';

type TextEditorProps = {
  defaultValue?: string;
  onChange: (value: string) => void;
  wordsToHighlight?: string[];
  disabled?: boolean;
};

function createHighlightPlugin(decorator: MatchDecorator) {
  return ViewPlugin.fromClass(
    class {
      decorations;

      constructor(view: EditorView) {
        this.decorations = decorator.createDeco(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = decorator.createDeco(update.view);
        }
      }
    },
    {
      decorations: (v) => v.decorations
    }
  );
}

export default function TextEditor({
  defaultValue,
  onChange,
  wordsToHighlight,
  disabled
}: TextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editorRef.current) return undefined;

    const validWordsPattern = wordsToHighlight?.length
      ? `\\{\\{(${wordsToHighlight.join('|')})\\}\\}`
      : '';

    const validWordsRegex = validWordsPattern
      ? new RegExp(validWordsPattern, 'g')
      : null;

    const invalidWordsPattern = wordsToHighlight?.length
      ? `\\{\\{(?!(${wordsToHighlight.join('|')}))([^{}]+)\\}\\}`
      : '';

    const invalidWordsRegex = invalidWordsPattern
      ? new RegExp(invalidWordsPattern, 'g')
      : null;

    const systemTagRegex = /<\/?system>/g;
    const userTagRegex = /<\/?user>/g;
    const imageTagRegex = /<\/?image>/g;
    const paramExistsOpenTagRegex = /<param-exists:.*?>/g;
    const paramExistsCloseTagRegex = /<\/param-exists>/g;

    const extensions = [
      keymap.of([...defaultKeymap, ...historyKeymap]),
      history(),
      EditorView.lineWrapping,
      lineNumbers(),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onChange(update.state.doc.toString());
        }
      })
    ];

    if (validWordsRegex) {
      const validWordDecorator = new MatchDecorator({
        regexp: validWordsRegex,
        decoration: Decoration.mark({ class: 'valid-word' })
      });

      extensions.push(createHighlightPlugin(validWordDecorator));
    }

    if (invalidWordsRegex) {
      const invalidWordDecorator = new MatchDecorator({
        regexp: invalidWordsRegex,
        decoration: Decoration.mark({ class: 'invalid-word' })
      });

      extensions.push(createHighlightPlugin(invalidWordDecorator));
    }

    const systemTagDecorator = new MatchDecorator({
      regexp: systemTagRegex,
      decoration: Decoration.mark({ class: 'system-tag' })
    });
    extensions.push(createHighlightPlugin(systemTagDecorator));

    const userTagDecorator = new MatchDecorator({
      regexp: userTagRegex,
      decoration: Decoration.mark({ class: 'user-tag' })
    });
    extensions.push(createHighlightPlugin(userTagDecorator));

    const imageTagDecorator = new MatchDecorator({
      regexp: imageTagRegex,
      decoration: Decoration.mark({ class: 'image-tag' })
    });
    extensions.push(createHighlightPlugin(imageTagDecorator));

    const paramExistsOpenTagDecorator = new MatchDecorator({
      regexp: paramExistsOpenTagRegex,
      decoration: Decoration.mark({ class: 'param-exists-tag' })
    });
    extensions.push(createHighlightPlugin(paramExistsOpenTagDecorator));

    const paramExistsCloseTagDecorator = new MatchDecorator({
      regexp: paramExistsCloseTagRegex,
      decoration: Decoration.mark({ class: 'param-exists-tag' })
    });
    extensions.push(createHighlightPlugin(paramExistsCloseTagDecorator));

    const state = EditorState.create({
      doc: defaultValue || '',
      extensions
    });

    const view = new EditorView({
      state,
      parent: editorRef.current
    });

    return () => {
      view.destroy();
    };
  }, [wordsToHighlight]);

  return (
    <>
      <div
        ref={editorRef}
        className={`h-full overflow-auto ${
          disabled && 'pointer-events-none opacity-50'
        }`}
      />
      <style>
        {`
          .cm-content .valid-word {
            color: blue;
            font-weight: bold;
          }
          .cm-content .invalid-word {
            color: red;
            font-weight: bold;
          }
          .cm-content .system-tag {
            color: #8a2be2; /* BlueViolet */
            font-weight: bold;
          }
          .cm-content .user-tag {
            color: #1e90ff; /* DodgerBlue */
            font-weight: bold;
          }
          .cm-content .image-tag {
            color: #2e8b57; /* SeaGreen */
            font-weight: bold;
          }
          .cm-content .param-exists-tag {
            color: #ff8c00; /* DarkOrange */
            font-weight: bold;
          }
          .cm-editor {
            height: 100%;
          }
          .cm-scroller {
            overflow: auto;
          }
          .cm-line {
            line-height: 1.7;
          }
        `}
      </style>
    </>
  );
}
